import React from 'react'
import { createStore } from 'redux'
import { Provider, useDispatch, useSelector } from 'react-redux'
import { produce, enableMapSet } from 'immer'

import { calculateBinary } from '../../../../common/utils/NumberUtils'
import { securitiesEmployeeType as securitiesEmployeeTypeConst } from '../../../../_constants/Code'
import './types'
import { getPreviousFiscalYear } from './fiscalYearUtils'
import {
  groupDataByFiscalYear,
  getFiscalYearSettings,
  createPrescribedWorkingHoursMonthTemplate,
  generateEmploymentTypeSettings,
  arrayToMap,
  copyChangedFields
} from './helpers'

enableMapSet()

/*==================================
 定数定義
 ==================================*/
// 有報従業員区分の選択肢（対象外）
const excluded = securitiesEmployeeTypeConst.excluded.name

/*==================================
  初期状態
===================================*/
/** @type {EmployeeCountInfoState} */
export const initialState = {
  employeeCountInfo: {
    selectedFiscalYear: '',
    dataFixStatus: '',
    fiscalYearList: [],
    fiscalYearSettings: {},
    standardFormatSecondmentList: []
  }
}

/** 雇用区分設定のデフォルト値 */
export const tempEmploymentTypeSetting = [
  {
    fiscalYear: '',
    fiscalYearMonth: '',
    employmentType: '',
    securitiesEmployeeType: '',
    fteEstimatedFactor: ''
  }
]

/** 所定労働時間設定（月）デフォルト値 */
export const tempPrescribedWorkingHoursMonth = [
  {
    fiscalYear: '',
    fiscalYearMonth: '',
    prescribedWorkingHoursPerDay: '',
    month: '',
    workingDays: '',
    prescribedWorkingHoursPerMonth: ''
  }
]

/** 所定労働時間設定（年）デフォルト値 */
export const tempPrescribedWorkingHoursYear = [
  {
    fiscalYear: '',
    fiscalYearMonth: '',
    weeksPerYear: '',
    prescribedWorkingHoursPerWeek: '',
    prescribedWorkingHoursPerYear: ''
  }
]

/** 人数カウント設定デフォルト値 */
export const tempEmployeeCountSetting = [
  {
    fiscalYear: '',
    fiscalYearMonth: '',
    roundingMethod: '切り捨て',
    employeeOnLeaveTarget: '',
    intraGroupSecondmentSource: '',
    intraGroupSecondmentDestination: '',
    interGroupSecondmentSource: '',
    interGroupSecondmentDestination: '',
    concurrentSecondmentTarget: ''
  }
]

/**
 * @function マッピング設定の状態管理
 * @param {typeof initialState} state
 * @param {{ type: string; payload: unknown }} action
 * @returns {typeof initialState}
 */
export function employeeCountReducer(state = initialState, action) {
  return produce(state, (draft) => {
    switch (action.type) {
      case 'INIT_STATE': {
        const {
          accPeriod = '',
          selectedFiscalYear = '',
          annualTask = [],
          fiscalYearDataList = [],
          standardFormatSecondmentList = [],
          employmentType = [],
          employmentTypeSetting = [],
          prescribedWorkingHoursMonth = [],
          prescribedWorkingHoursYear = [],
          employeeCountSetting = []
        } = action.payload

        // データを年度ごとにグループ化
        const groupedData = {
          annualTask: groupDataByFiscalYear(annualTask),
          employmentTypeSetting: groupDataByFiscalYear(employmentTypeSetting),
          prescribedWorkingHoursMonth: groupDataByFiscalYear(prescribedWorkingHoursMonth),
          prescribedWorkingHoursYear: groupDataByFiscalYear(prescribedWorkingHoursYear),
          employeeCountSetting: groupDataByFiscalYear(employeeCountSetting)
        }

        // 基本情報のセット
        draft.employeeCountInfo.selectedFiscalYear = selectedFiscalYear
        draft.employeeCountInfo.fiscalYearList = fiscalYearDataList.map((item) => item.fiscalYear)
        draft.employeeCountInfo.dataFixStatus = groupedData.annualTask[selectedFiscalYear]?.[0]?.DataFixStatus ?? null
        draft.employeeCountInfo.standardFormatSecondmentList = standardFormatSecondmentList

        // fiscalYearListをループして、年度ごとにデータを整理
        fiscalYearDataList.forEach(({ fiscalYear, fiscalYearStartMonth, fiscalYearMonth }) => {
          // 各設定情報から、現在の年度に一致するデータを抽出
          const currentAnnualTask = groupedData.annualTask[fiscalYear] || []
          const currentEmploymentTypeSetting = groupedData.employmentTypeSetting[fiscalYear] || []
          const currentPrescribedWorkingHoursMonth = groupedData.prescribedWorkingHoursMonth[fiscalYear] || []
          const currentPrescribedWorkingHoursYear = groupedData.prescribedWorkingHoursYear[fiscalYear] || []
          const currentEmployeeCountSetting = groupedData.employeeCountSetting[fiscalYear] || []

          // APIデータが存在しない場合は、デフォルト値を適用する
          // 雇用区分設定
          const initEmploymentTypeSetting = generateEmploymentTypeSettings(
            employmentType,
            currentEmploymentTypeSetting,
            fiscalYear,
            fiscalYearStartMonth,
            fiscalYearMonth
          )

          // 所定労働時間設定（月）
          const initPrescribedWorkingHoursMonth =
            currentPrescribedWorkingHoursMonth.length > 0
              ? currentPrescribedWorkingHoursMonth
              : createPrescribedWorkingHoursMonthTemplate(accPeriod, fiscalYear, fiscalYearMonth)

          // 定労働時間設定（年）
          const initPrescribedWorkingHoursYear =
            currentPrescribedWorkingHoursYear.length > 0
              ? currentPrescribedWorkingHoursYear
              : tempPrescribedWorkingHoursYear.map((item) => ({
                  ...item,
                  fiscalYear,
                  fiscalYearMonth
                }))

          // 人数カウント設定
          const initEmployeeCountSetting =
            currentEmployeeCountSetting.length > 0
              ? currentEmployeeCountSetting
              : tempEmployeeCountSetting.map((item) => ({
                  ...item,
                  fiscalYear,
                  fiscalYearMonth
                }))

          // 初期表示時の状態を設定
          draft.employeeCountInfo.fiscalYearSettings[fiscalYear] = {
            employmentTypeSetting: initEmploymentTypeSetting,
            prescribedWorkingHoursMonth: initPrescribedWorkingHoursMonth,
            prescribedWorkingHoursYear: initPrescribedWorkingHoursYear,
            employeeCountSetting: initEmployeeCountSetting,
            dataFixStatus: currentAnnualTask.length > 0 ? currentAnnualTask[0].DataFixStatus : null,
            errors: {}
          }
        })

        break
      }
      // 前年コピー処理
      case 'COPY_PREVIOUS_FISCAL_YEAR': {
        const selectedFiscalYear = draft.employeeCountInfo.selectedFiscalYear
        if (!selectedFiscalYear) break

        /**
         * 選択年度の前年の fiscalYearSettings から 以下の設定をコピーして現在年度に反映する
         * ・employmentTypeSetting：employmentTypeをキーにsecuritiesEmployeeType、fteEstimatedFactorを差分更新
         * ・prescribedWorkingHoursMonth：monthをキーにprescribedWorkingHoursPerDay、workingDays、prescribedWorkingHoursPerMonthを差分更新
         * ・prescribedWorkingHoursYear（配列[0]）：weeksPerYear、prescribedWorkingHoursPerWeek、prescribedWorkingHoursPerYearを差分更新
         * ・employeeCountSetting（配列[0]）：roundingMethod、employeeOnLeaveTarget、intraGroupSecondmentSource、
         * intraGroupSecondmentDestination、interGroupSecondmentSource、interGroupSecondmentDestination、concurrentSecondmentTargetを差分更新
         */

        // 前年度の年度文字列を取得
        const prevFiscalYear = getPreviousFiscalYear(selectedFiscalYear)
        if (!prevFiscalYear) break

        const prevSettings = draft.employeeCountInfo.fiscalYearSettings[prevFiscalYear]
        const currentSettings = draft.employeeCountInfo.fiscalYearSettings[selectedFiscalYear]
        if (!prevSettings || !currentSettings) break

        // 配列を Map に変換
        const prevEmpMap = arrayToMap(prevSettings.employmentTypeSetting, 'employmentType')
        const curEmpMap = arrayToMap(currentSettings.employmentTypeSetting, 'employmentType')

        /*  ===== 雇用区分設定情報コピー  ===== */
        prevEmpMap.forEach((prevEmp, empType) => {
          const curEmp = curEmpMap.get(empType)
          if (!curEmp) return

          copyChangedFields(curEmp, prevEmp, [
            'securitiesEmployeeType',
            'fteEstimatedFactor',
            'fteActualWorkingHoursCalcMethod'
          ])
        })

        /*  ===== 所定労働時間設定情報（月）コピー  ===== */
        const prevMonthMap = arrayToMap(prevSettings.prescribedWorkingHoursMonth, 'month')
        const curMonthMap = arrayToMap(currentSettings.prescribedWorkingHoursMonth, 'month')

        prevMonthMap.forEach((prevMonth, month) => {
          const curMonth = curMonthMap.get(month)
          if (!curMonth) return

          copyChangedFields(curMonth, prevMonth, [
            'prescribedWorkingHoursPerDay',
            'workingDays',
            'prescribedWorkingHoursPerMonth'
          ])
        })

        /*  ===== 所定労働時間設定情報（年）コピー  ===== */
        if (prevSettings.prescribedWorkingHoursYear[0] && currentSettings.prescribedWorkingHoursYear[0]) {
          copyChangedFields(currentSettings.prescribedWorkingHoursYear[0], prevSettings.prescribedWorkingHoursYear[0], [
            'weeksPerYear',
            'prescribedWorkingHoursPerWeek',
            'prescribedWorkingHoursPerYear'
          ])
        }

        /*  ===== 人数カウント設定情報コピー  ===== */
        if (prevSettings.employeeCountSetting[0] && currentSettings.employeeCountSetting[0]) {
          copyChangedFields(currentSettings.employeeCountSetting[0], prevSettings.employeeCountSetting[0], [
            'roundingMethod',
            'employeeOnLeaveTarget',
            'intraGroupSecondmentSource',
            'intraGroupSecondmentDestination',
            'interGroupSecondmentSource',
            'interGroupSecondmentDestination',
            'concurrentSecondmentTarget'
          ])
        }

        break
      }
      // 年度リスト選択時のアクション
      case 'CHANGE_FISCAL_YEAR': {
        const { fiscalYear: selectedFiscalYear } = action.payload
        draft.employeeCountInfo.selectedFiscalYear = selectedFiscalYear

        const fiscalYearSettings = getFiscalYearSettings(draft, draft.employeeCountInfo.selectedFiscalYear)

        fiscalYearSettings.errors.server = {}
        fiscalYearSettings.errors.client = {}

        break
      }
      // 小数点の扱いに関する設定変更時のアクション
      case 'UPDATE_ROUNDING_METHOD': {
        const { roundingMethod } = action.payload
        const fiscalYearSettings = getFiscalYearSettings(draft, draft.employeeCountInfo.selectedFiscalYear)
        if (fiscalYearSettings?.employeeCountSetting?.[0]) {
          fiscalYearSettings.employeeCountSetting[0].roundingMethod = roundingMethod
        }
        break
      }
      // 有報従業員区分リスト選択時のアクション
      case 'CHANGE_SECURITIES_EMP_TYPE': {
        const { employmentType, securitiesEmployeeType } = action.payload
        const fiscalYearSettings = getFiscalYearSettings(draft, draft.employeeCountInfo.selectedFiscalYear)
        const empTypeSetting = fiscalYearSettings?.employmentTypeSetting?.find(
          (item) => item.employmentType === employmentType
        )
        if (empTypeSetting) {
          empTypeSetting.securitiesEmployeeType = securitiesEmployeeType
        }
        break
      }

      case 'CHANGE_FTE_ACTUAL_WORKING_HOURS_CALC_METHOD': {
        const { employmentType, fteActualWorkingHoursCalcMethod } = action.payload
        const fiscalYearSettings = getFiscalYearSettings(draft, draft.employeeCountInfo.selectedFiscalYear)
        const empTypeSetting = fiscalYearSettings?.employmentTypeSetting?.find(
          (item) => item.employmentType === employmentType
        )
        if (empTypeSetting) {
          empTypeSetting.fteActualWorkingHoursCalcMethod = fteActualWorkingHoursCalcMethod
        }
        break
      }
      // FTE概算係数変更時のアクション
      case 'CHANGE_FTE_ESTIMATED_FACTOR': {
        const { employmentType, fteEstimatedFactor } = action.payload
        const fiscalYearSettings = getFiscalYearSettings(draft, draft.employeeCountInfo.selectedFiscalYear)
        const empTypeSetting = fiscalYearSettings?.employmentTypeSetting?.find(
          (item) => item.employmentType === employmentType
        )
        if (empTypeSetting) {
          empTypeSetting.fteEstimatedFactor = fteEstimatedFactor
        }
        break
      }
      // 所定労働時間設定（日）変更時のアクション
      case 'CHANGE_WORKING_HOURS_PER_DAY': {
        const { prescribedWorkingHoursPerDay } = action.payload
        const fiscalYearSettings = getFiscalYearSettings(draft, draft.employeeCountInfo.selectedFiscalYear)
        if (fiscalYearSettings?.prescribedWorkingHoursMonth) {
          fiscalYearSettings.prescribedWorkingHoursMonth.forEach((item) => {
            item.prescribedWorkingHoursPerDay = prescribedWorkingHoursPerDay
            // 所定労働時間（月）の再計算
            const workingDaysNum = Number(item.workingDays) || 0
            const prescribedHoursNum = Number(prescribedWorkingHoursPerDay) || 0
            item.prescribedWorkingHoursPerMonth = calculateBinary(workingDaysNum, prescribedHoursNum, '*')
          })
        }
        break
      }
      // 営業日数変更時のアクション
      case 'CHANGE_WORKING_DAY': {
        const { month, workingDays } = action.payload
        const fiscalYearSettings = getFiscalYearSettings(draft, draft.employeeCountInfo.selectedFiscalYear)
        if (fiscalYearSettings?.prescribedWorkingHoursMonth) {
          const item = fiscalYearSettings.prescribedWorkingHoursMonth.find((i) => i.month === month)
          if (item) {
            item.workingDays = workingDays
            // 所定労働時間（月）の再計算
            const workingDaysNum = Number(workingDays) || 0
            const prescribedHoursNum = Number(item.prescribedWorkingHoursPerDay) || 0
            item.prescribedWorkingHoursPerMonth = calculateBinary(workingDaysNum, prescribedHoursNum, '*')
          }
        }
        break
      }
      // 営業日数一括設定時のアクション
      case 'BULK_SET_WORKING_DAY': {
        const { value: workingDays } = action.payload
        const newWorkingDays = workingDays

        if (isNaN(Number(newWorkingDays))) {
          // 数値でなければ処理をスキップ
          break
        }

        const fiscalYearSettings = getFiscalYearSettings(draft, draft.employeeCountInfo.selectedFiscalYear)
        if (fiscalYearSettings?.prescribedWorkingHoursMonth) {
          fiscalYearSettings.prescribedWorkingHoursMonth.forEach((item) => {
            item.workingDays = newWorkingDays
            // 所定労働時間（月）の再計算
            const workingDaysNum = Number(item.workingDays) || 0
            const prescribedHoursNum = Number(item.prescribedWorkingHoursPerDay) || 0
            item.prescribedWorkingHoursPerMonth = calculateBinary(workingDaysNum, prescribedHoursNum, '*')
          })
        }
        break
      }
      // 年間週数変更時のアクション
      case 'CHANGE_WEEKS_PER_YEAR': {
        const { weeksPerYear } = action.payload
        const fiscalYearSettings = getFiscalYearSettings(draft, draft.employeeCountInfo.selectedFiscalYear)
        if (fiscalYearSettings?.prescribedWorkingHoursYear?.[0]) {
          const prescribedWorkingHoursYearItem = fiscalYearSettings.prescribedWorkingHoursYear[0]

          // 年間週数を更新
          prescribedWorkingHoursYearItem.weeksPerYear = weeksPerYear

          // // 所定労働時間（年）を再計算
          const weeksPerYearNum = Number(weeksPerYear) || 0
          const prescribedHoursPerWeekNum = Number(prescribedWorkingHoursYearItem.prescribedWorkingHoursPerWeek) || 0

          // 計算結果を年間の所定労働時間として更新
          prescribedWorkingHoursYearItem.prescribedWorkingHoursPerYear = calculateBinary(
            weeksPerYearNum,
            prescribedHoursPerWeekNum,
            '*'
          )
        }
        break
      }
      // 「所定労働時間／週」変更時のアクション
      case 'CHANGE_PRESCRIBED_HOURS_PER_WEEK': {
        const { prescribedWorkingHoursPerWeek } = action.payload
        const fiscalYearSettings = getFiscalYearSettings(draft, draft.employeeCountInfo.selectedFiscalYear)
        if (fiscalYearSettings?.prescribedWorkingHoursYear?.[0]) {
          const prescribedWorkingHoursYearItem = fiscalYearSettings.prescribedWorkingHoursYear[0]

          // 「所定労働時間／週」を更新
          prescribedWorkingHoursYearItem.prescribedWorkingHoursPerWeek = prescribedWorkingHoursPerWeek

          // 所定労働時間（年）を再計算
          const weeksPerYearNum = Number(prescribedWorkingHoursYearItem.weeksPerYear) || 0
          const prescribedHoursPerWeekNum = Number(prescribedWorkingHoursPerWeek) || 0

          // 計算結果を年間の所定労働時間として更新
          prescribedWorkingHoursYearItem.prescribedWorkingHoursPerYear = calculateBinary(
            weeksPerYearNum,
            prescribedHoursPerWeekNum,
            '*'
          )
        }
        break
      }
      // 休職者設定リスト選択時のアクション
      case 'CHANGE_EMPLOYEE_ON_LEAVE': {
        const { employeeOnLeaveTarget } = action.payload
        const fiscalYearSettings = getFiscalYearSettings(draft, draft.employeeCountInfo.selectedFiscalYear)
        if (fiscalYearSettings?.employeeCountSetting?.[0]) {
          fiscalYearSettings.employeeCountSetting[0].employeeOnLeaveTarget = employeeOnLeaveTarget
        }
        break
      }
      // グループ内出向元の設定変更時のアクション ※出向元と出向先の値を同時に更新
      case 'CHANGE_INTRA_GROUP_SECONDMENT': {
        const { intraGroupSecondmentSource, intraGroupSecondmentDestination } = action.payload
        const fiscalYearSettings = getFiscalYearSettings(draft, draft.employeeCountInfo.selectedFiscalYear)
        if (fiscalYearSettings?.employeeCountSetting?.[0]) {
          fiscalYearSettings.employeeCountSetting[0].intraGroupSecondmentSource = intraGroupSecondmentSource
          fiscalYearSettings.employeeCountSetting[0].intraGroupSecondmentDestination = intraGroupSecondmentDestination
        }
        break
      }
      // グループ外出向元の設定変更時のアクション
      case 'CHANGE_INTER_GROUP_SECONDMENT_SOURCE': {
        const { interGroupSecondmentSource } = action.payload
        const fiscalYearSettings = getFiscalYearSettings(draft, draft.employeeCountInfo.selectedFiscalYear)
        if (fiscalYearSettings?.employeeCountSetting?.[0]) {
          fiscalYearSettings.employeeCountSetting[0].interGroupSecondmentSource = interGroupSecondmentSource
        }
        break
      }
      // グループ外出向先の設定変更時のアクション
      case 'CHANGE_INTER_GROUP_SECONDMENT_DESTINATION': {
        const { interGroupSecondmentDestination } = action.payload
        const fiscalYearSettings = getFiscalYearSettings(draft, draft.employeeCountInfo.selectedFiscalYear)
        if (fiscalYearSettings?.employeeCountSetting?.[0]) {
          fiscalYearSettings.employeeCountSetting[0].interGroupSecondmentDestination = interGroupSecondmentDestination
        }
        break
      }
      // 出向者の設定（兼務出向）の設定変更時のアクション
      case 'CHANGE_CONCURRENT_SECONDMENT_TARGET': {
        const { concurrentSecondmentTarget } = action.payload
        const fiscalYearSettings = getFiscalYearSettings(draft, draft.employeeCountInfo.selectedFiscalYear)
        if (fiscalYearSettings?.employeeCountSetting?.[0]) {
          fiscalYearSettings.employeeCountSetting[0].concurrentSecondmentTarget = concurrentSecondmentTarget
        }
        break
      }
      // クライアントエラー発生時のアクション
      case 'SET_CLIENT_ERROR': {
        const { errors } = action.payload
        const fiscalYearSettings = getFiscalYearSettings(draft, draft.employeeCountInfo.selectedFiscalYear)
        if (fiscalYearSettings?.errors) {
          fiscalYearSettings.errors.client = errors
        } else {
          fiscalYearSettings.errors.client = {}
        }
        break
      }
      // クライアントエラークリアアクション
      case 'CLEAR_CLIENT_ERROR': {
        const fiscalYearSettings = getFiscalYearSettings(draft, draft.employeeCountInfo.selectedFiscalYear)
        if (fiscalYearSettings?.errors) {
          fiscalYearSettings.errors.client = {}
        }
        break
      }
      // サーバーエラー発生時のアクション
      case 'SET_SERVER_ERROR': {
        const { serverErrorMsg } = action.payload
        const errors = { messages: [serverErrorMsg] }
        const fiscalYearSettings = getFiscalYearSettings(draft, draft.employeeCountInfo.selectedFiscalYear)
        if (fiscalYearSettings?.errors) {
          fiscalYearSettings.errors.server = errors
        } else {
          fiscalYearSettings.errors.server = {}
        }
        break
      }
      default: {
        return draft
      }
    }
  })
}

/** @returns {function (string, any)=>void} */
export const useEmployeeCountDispatch = () => {
  const _dispatch = useDispatch()
  return (type, payload) => _dispatch({ type, payload })
}

/** @type {TypedUseSelectorHook<EmployeeCountInfoState>} */
export const useEmployeeCountSelector = useSelector

export const EmployeeCountInfoProviderStore = (props) => {
  const [store] = React.useState(() =>
    createStore(employeeCountReducer, window.__REDUX_DEVTOOLS_EXTENSION__ && window.__REDUX_DEVTOOLS_EXTENSION__())
  )
  return <Provider store={store}>{props.children}</Provider>
}
